import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { KanbanCard } from '@/components/tasks/KanbanCard';


interface KanbanColumnProps {
  status: {
    id: string;
    name: string;
    color: string;
    isClosed: boolean;
  };
  tasks: Array<{
    id: string;
    title: string;
    priority?: string;
    dueDate?: string;
    status?: string;
    statusConfig?: { isClosed?: boolean };
    assignee?: { id: string; name: string; avatarUrl?: string };
    assigneeId?: string;
    assignees?: Array<{ id: string; name: string; avatarUrl?: string }>;
    _count?: { comments?: number };
  }>;
  projectMembers?: Array<{ user: { id: string; name: string; avatarUrl?: string } }>;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onTaskClick?: (task: any) => void;
  onTaskAssigneeChange?: (taskId: string, assigneeId: string | null) => void;
  onTaskAssigneesChange?: (taskId: string, assigneeIds: string[]) => void;
  onTaskDueDateChange?: (taskId: string, dueDate: string | null) => void;
  onTaskPriorityChange?: (taskId: string, priority: string) => void;
  onAddTask?: () => void;
  selectionMode?: boolean;
  selectedIds?: Set<string>;
  onSelectTask?: (taskId: string, shiftKey: boolean) => void;
  /** Cards hidden while a group drag is in flight. */
  hiddenIds?: Set<string>;
}

export function KanbanColumn({
  status,
  tasks,
  projectMembers,
  collapsed = false,
  onToggleCollapse,
  onTaskClick,
  onTaskAssigneeChange,
  onTaskAssigneesChange,
  onTaskDueDateChange,
  onTaskPriorityChange,
  onAddTask,
  selectionMode,
  selectedIds,
  onSelectTask,
  hiddenIds,
}: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: status.id,
  });

  // Collapsed: a narrow vertical strip that still acts as a drop target, so
  // cards can be dragged onto it without the column taking up full width.
  if (collapsed) {
    return (
      <div
        ref={setNodeRef}
        onClick={onToggleCollapse}
        title={`${status.name} (${tasks.length})`}
        className={`flex-shrink-0 w-12 min-h-[24rem] flex flex-col items-center gap-2 py-3 rounded-lg border cursor-pointer transition-colors ${
          isOver ? 'bg-accent/10 border-accent/30' : 'bg-grey-50 border-grey-100 hover:bg-grey-100'
        }`}
      >
        <ChevronRight className="w-4 h-4 text-grey-500 flex-shrink-0" />
        <span
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: status.color }}
        />
        <span
          className="text-sm font-medium text-secondary whitespace-nowrap"
          style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
        >
          {status.name}
        </span>
        <span className="px-2 py-0.5 bg-grey-200 text-grey-600 text-xs font-medium rounded-full flex-shrink-0">
          {tasks.length}
        </span>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      className={`flex-shrink-0 w-64 min-h-[24rem] flex flex-col rounded-lg overflow-hidden border transition-colors ${
        isOver ? 'bg-accent/5 border-accent/30' : 'bg-grey-50 border-grey-100'
      }`}
    >
      {/* Header */}
      <div
        className="p-3 flex items-center justify-between cursor-pointer hover:bg-grey-100 transition-colors"
        onClick={onToggleCollapse}
        style={{ borderLeftWidth: '4px', borderLeftColor: status.color }}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <button className="text-grey-600 hover:text-secondary p-0" title="Collapse column">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h3 className="font-medium text-secondary truncate">{status.name}</h3>
          <span className="px-2 py-0.5 bg-grey-200 text-grey-600 text-xs font-medium rounded-full flex-shrink-0">
            {tasks.length}
          </span>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onAddTask?.();
          }}
          className="p-1 text-grey-600 hover:text-secondary hover:bg-white rounded transition-colors flex-shrink-0"
          title="Add task"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Body - Scrollable task list */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {tasks.length > 0 ? (
          <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
            {tasks.map((task) => (
              <KanbanCard
                key={task.id}
                task={task}
                projectMembers={projectMembers}
                onClick={() => onTaskClick?.(task)}
                onAssigneeChange={
                  onTaskAssigneeChange
                    ? (assigneeId) => onTaskAssigneeChange(task.id, assigneeId)
                    : undefined
                }
                onAssigneesChange={
                  onTaskAssigneesChange
                    ? (assigneeIds) => onTaskAssigneesChange(task.id, assigneeIds)
                    : undefined
                }
                onDueDateChange={
                  onTaskDueDateChange
                    ? (dueDate) => onTaskDueDateChange(task.id, dueDate)
                    : undefined
                }
                onPriorityChange={
                  onTaskPriorityChange
                    ? (priority) => onTaskPriorityChange(task.id, priority)
                    : undefined
                }
                selectionMode={selectionMode}
                selected={selectedIds?.has(task.id)}
                onSelectChange={
                  onSelectTask ? (_checked, shiftKey) => onSelectTask(task.id, shiftKey) : undefined
                }
                dragHidden={hiddenIds?.has(task.id)}
              />
            ))}
          </SortableContext>
        ) : (
          <div className="flex items-center justify-center h-24 border-2 border-dashed border-grey-200 rounded-lg text-grey-400 text-sm">
            No tasks
          </div>
        )}
      </div>
    </div>
  );
}
